/**
 * Balance-over-time line for a loan. Presentational: the caller gives the points (payment number and remaining balance).
 * Long schedules are thinned to at most 120 points for drawing; the text description and the end points stay exact.
 */
export interface BalancePoint { n: number; balance: number }
export function thinPoints<T>(points: T[], max = 120): T[] {
  if (points.length <= max) return points;
  const step = (points.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => points[Math.round(i * step)]);
}
export function BalanceLine({ points, money, caption = "Remaining balance over time", startLabel, endLabel }: { points: BalancePoint[]; money: (n: number) => string; caption?: string; startLabel: string; endLabel: string }) {
  if (points.length < 2) return null;
  const W = 320, H = 120, L = 6, T = 8, B = 18;
  const drawn = thinPoints(points);
  const max = Math.max(1, ...drawn.map((p) => p.balance)), last = drawn[drawn.length - 1].n || 1;
  const x = (n: number) => L + (n / last) * (W - 2 * L), y = (b: number) => T + (1 - b / max) * (H - T - B);
  const alt = `Chart of the remaining loan balance. It starts at ${money(points[0].balance)} at ${startLabel} and reaches ${money(points[points.length - 1].balance)} at ${endLabel}.`;
  return (
    <figure data-testid="balance-line">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={alt} className="w-full text-primary">
        <polyline fill="none" stroke="currentColor" strokeWidth={2} points={drawn.map((p) => `${x(p.n)},${y(p.balance)}`).join(" ")} />
        <text x={L} y={H - 4} fontSize={9} fill="currentColor" className="text-muted-foreground">{startLabel}</text>
        <text x={W - L} y={H - 4} fontSize={9} textAnchor="end" fill="currentColor" className="text-muted-foreground">{endLabel}</text>
      </svg>
      <figcaption className="text-xs text-muted-foreground text-center">{caption}</figcaption>
    </figure>
  );
}
